import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Checkbox,
  ListItemText
} from '@mui/material';

type Option = {id: string | number; name: string};

type FieldGrid = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

type FieldConfig = {
  order: number;
  grid?: FieldGrid;
  heading: [string, string];
  placeholder: [string, string];
  type: 'text' | 'email' | 'select';
  multiple?: boolean;
  required?: boolean;
  validation?: {
    min_length?: number;
    max_length?: number;
    pattern?: string;
  };
  options?: Option[];
  sql_data?: {
    sql: string;
    variables: Array<{data_column: string; req_column: string}>;
    schema: string[];
  };
};

type AddNewFormConfig = {
  module_name: string;
  title: string;
  cta_text: string;
  all_fields_required?: boolean;
  fields: Record<string, FieldConfig>;
  staticOptions?: Record<string, Option[]>;
};

export interface NewUserFormProps {
  config: AddNewFormConfig;
  onSubmit: (payload: Record<string, any>) => void;
  onCancel: () => void;
  // eslint-disable-next-line react/boolean-prop-naming
  loading?: boolean;
  initialValues?: Record<string, any>;
  loadOptions?: (fieldName: string, field: FieldConfig) => Promise<Option[]>;
}

const NewUserForm: React.FC<NewUserFormProps> = ({
  config,
  onSubmit,
  onCancel,
  loading,
  initialValues = {},
  loadOptions
}) => {
  const [values, setValues] =
    React.useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [optionsMap, setOptionsMap] = React.useState<Record<string, Option[]>>(
    {}
  );

  const fieldEntries = React.useMemo(
    () => Object.entries(config.fields).sort((a, b) => a[1].order - b[1].order),
    [config.fields]
  );

  const getValueKey = (fieldName: string): keyof Option =>
    fieldName === 'organization'
      ? 'id'
      : fieldName === 'user_group'
        ? 'name'
        : 'id';

  const isSelected = (arr: any[], v: string | number) =>
    Array.isArray(arr) && arr.some(x => `${x}` === `${v}`);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const map: Record<string, Option[]> = {};
      for (const [name, field] of fieldEntries) {
        if (field.type === 'select') {
          const staticOpts =
            config.staticOptions?.[name] || field.options || [];
          if (staticOpts.length) {
            map[name] = staticOpts;
          } else if (loadOptions) {
            try {
              map[name] = await loadOptions(name, field);
            } catch {
              map[name] = [];
            }
          } else {
            map[name] = [];
          }
        }
      }
      if (mounted) setOptionsMap(map);
    })();
    return () => {
      mounted = false;
    };
  }, [config.staticOptions, fieldEntries, loadOptions]);

  const validateField = (
    name: string,
    field: FieldConfig,
    value: any
  ): string => {
    if (
      field.required &&
      (value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0))
    ) {
      return 'This field is required';
    }
    if (typeof value === 'string') {
      if (
        field.validation?.min_length &&
        value.length < field.validation.min_length
      ) {
        return `Minimum ${field.validation.min_length} characters`;
      }
      if (
        field.validation?.max_length &&
        value.length > field.validation.max_length
      ) {
        return `Maximum ${field.validation.max_length} characters`;
      }
      if (field.validation?.pattern) {
        const re = new RegExp(field.validation.pattern);
        if (!re.test(value)) return 'Invalid format';
      }
    }
    return '';
  };

  const handleChange = (name: string, field: FieldConfig) => (e: any) => {
    const val =
      field.type === 'select' && field.multiple
        ? (e.target.value as any[])
        : e.target.value;
    setValues(prev => ({...prev, [name]: val}));
    const err = validateField(name, field, val);
    setErrors(prev => ({...prev, [name]: err}));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    for (const [name, field] of fieldEntries) {
      const err = validateField(name, field, values[name]);
      if (err) newErrors[name] = err;
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) onSubmit(values);
  };

  return (
    <Box display="flex" justifyContent="center" mt={2}>
      <Card sx={{width: '100%', maxWidth: 720}}>
        <CardHeader
          title={config.title}
          sx={{
            my: 2
          }}
        />
        <CardContent sx={{pt: 1}}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              {fieldEntries.map(([name, field]) => {
                const label = field.heading?.[0] ?? name;
                const placeholder = field.placeholder?.[0] ?? '';
                const grid = field.grid || {xs: 12};

                if (field.type === 'select') {
                  const opts = optionsMap[name] || [];
                  const currentValue =
                    values[name] ?? (field.multiple ? [] : '');
                  const valueKey: keyof Option = getValueKey(name);

                  return (
                    <Grid item key={name} {...grid}>
                      <FormControl fullWidth error={Boolean(errors[name])}>
                        <InputLabel id={`${name}-label`}>{label}</InputLabel>
                        <Select
                          labelId={`${name}-label`}
                          id={`${name}-select`}
                          multiple={Boolean(field.multiple)}
                          value={currentValue}
                          input={
                            field.multiple ? (
                              <OutlinedInput label={label} />
                            ) : undefined
                          }
                          label={label}
                          onChange={handleChange(name, field)}
                          renderValue={selected => {
                            if (Array.isArray(selected)) {
                              const labels = selected.map(val => {
                                const opt = opts.find(
                                  o => `${o[valueKey]}` === `${val}`
                                );
                                return opt?.name ?? `${val}`;
                              });
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5
                                  }}>
                                  {labels.map(l => (
                                    <Chip key={l} label={l} />
                                  ))}
                                </Box>
                              );
                            }
                            const opt = opts.find(
                              o => `${o[valueKey]}` === `${selected}`
                            );
                            return opt?.name ?? '';
                          }}
                          placeholder={placeholder}>
                          {opts.map(o => {
                            const val = o[valueKey];
                            const checked = isSelected(currentValue, val);
                            return (
                              <MenuItem key={o.id} value={val}>
                                {field.multiple && (
                                  <Checkbox checked={checked} />
                                )}
                                <ListItemText primary={o.name} />
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {errors[name] && (
                          <FormHelperText>{errors[name]}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  );
                }

                // text / email
                return (
                  <Grid item key={name} {...grid}>
                    <TextField
                      fullWidth
                      type={field.type === 'email' ? 'email' : 'text'}
                      label={label}
                      placeholder={placeholder}
                      value={values[name] ?? ''}
                      onChange={handleChange(name, field)}
                      error={Boolean(errors[name])}
                      helperText={errors[name] || ' '}
                      required={field.required}
                    />
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  gap={2}
                  justifyContent="flex-end"
                  sx={{pt: 1}}>
                  <Button variant="text" color="inherit" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      config.cta_text || 'Add New User'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NewUserForm;
